import {
  getAllVaultsForAdmin,
  getAllProjects,
  getProjectResources,
  getMetaAdAccountsV23,
  getMetaFacebookPages,
  uploadMetaAdImage,
  createMetaCampaign,
  createMetaAdSet,
  createMetaAdCreative,
  createMetaAd,
} from 'api';
import { getCurrentBackendEnvironment } from 'helpers';
import NavigationBar from 'components/NavigationBar';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuthState } from 'stores/AuthStore';
import styled from 'styled-components';
import Select from 'react-select';
import { toPng } from 'html-to-image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faRocket, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import AdTemplate, { AdVariant } from './AdTemplate';
import PromptoLogo from 'resources/prompto-logo-full-black.svg';

// ─── Layout ──────────────────────────────────────────────────────────────────

const SPACING = 24;

const Container = styled.div`
  padding: 0 ${SPACING}px ${SPACING}px;
  background: ${({ theme }) => theme.background100};
  min-height: calc(100vh - 60px);
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${SPACING}px 0 ${SPACING - 8}px;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.boldTextColor};
  margin: 0;
`;

const ContentLayout = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
`;

const Sidebar = styled.div`
  width: 380px;
  flex-shrink: 0;
`;

const PreviewArea = styled.div`
  flex: 1;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

// ─── Cards ───────────────────────────────────────────────────────────────────

const SectionCard = styled.div`
  background: ${({ theme }) => theme.background};
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: ${SPACING}px;
  border: 1px solid ${({ theme }) => theme.background200};
`;

const SectionTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mainTextColor};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 16px;
`;

// ─── Form ────────────────────────────────────────────────────────────────────

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
`;

const InputLabel = styled.label`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.mainTextColor};
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.background300};
  border-radius: 6px;
  font-size: 0.85rem;
  background: ${({ theme }) => theme.background100};
  color: ${({ theme }) => theme.boldTextColor};
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.accentColor};
  }
`;

const DownloadButton = styled.button<{ loading?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  background: ${({ theme }) => theme.accentColor};
  color: ${({ theme }) => theme.boldTextColor};
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: ${({ loading }) => (loading ? 0.7 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── Preview ─────────────────────────────────────────────────────────────────

const PREVIEW_SCALE = 0.3;

const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PreviewContainer = styled.div`
  width: ${1080 * PREVIEW_SCALE}px;
  height: ${1920 * PREVIEW_SCALE}px;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`;

const ScaleWrapper = styled.div`
  transform: scale(${PREVIEW_SCALE});
  transform-origin: top left;
`;

const VariantLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.boldTextColor};
  text-transform: capitalize;
`;

// ─── Campaign Creation ──────────────────────────────────────────────────────

const CreateButton = styled.button<{ loading?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  background: #1877f2;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  opacity: ${({ loading }) => (loading ? 0.7 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StatusBox = styled.div<{ variant: 'info' | 'success' | 'error' }>`
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 0.8rem;
  line-height: 1.5;
  background: ${({ variant }) =>
    variant === 'success' ? '#e6f9e6' : variant === 'error' ? '#fde8e8' : '#e8f0fe'};
  color: ${({ variant }) =>
    variant === 'success' ? '#1a7a1a' : variant === 'error' ? '#c53030' : '#1a56db'};
  border: 1px solid
    ${({ variant }) =>
      variant === 'success' ? '#b7e4b7' : variant === 'error' ? '#f5c6c6' : '#b8d4fe'};
`;

const HousingNote = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.mainTextColor};
  opacity: 0.7;
  margin-top: 4px;
  line-height: 1.4;
`;

// ─── Country options ────────────────────────────────────────────────────────

const COUNTRY_OPTIONS = [
  { value: 'BE', label: 'Belgium' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'ES', label: 'Spain' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fetchImageAsDataUrl = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

const AdsPage = () => {
  const { AuthState } = useAuthState();
  const { sessionToken, user } = AuthState;

  const [allVaults, setAllVaults] = useState<any[]>([]);
  const [selectedVault, setSelectedVault] = useState<any>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const [ctaText, setCtaText] = useState('Ontdek dit project');
  const [description, setDescription] = useState('');
  const [locationText, setLocationText] = useState('');
  const [clientLogoDataUrl, setClientLogoDataUrl] = useState('');
  const [backgroundDataUrls, setBackgroundDataUrls] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Meta campaign state
  const [adAccounts, setAdAccounts] = useState<any[]>([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState<any>(null);
  const [facebookPages, setFacebookPages] = useState<any[]>([]);
  const [facebookPageId, setFacebookPageId] = useState('');
  const [dailyBudget, setDailyBudget] = useState('10');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [targetCountries, setTargetCountries] = useState<any[]>([
    COUNTRY_OPTIONS[0],
  ]);
  const [adLinkUrl, setAdLinkUrl] = useState('');
  const [adBodyText, setAdBodyText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [creationStep, setCreationStep] = useState('');
  const [creationError, setCreationError] = useState('');
  const [creationResult, setCreationResult] = useState<any>(null);

  const variantRefs = useRef<Record<AdVariant, HTMLDivElement | null>>({
    classic: null,
    minimal: null,
    bold: null,
  });

  // Fetch vaults on mount
  useEffect(() => {
    if (user?.vaultList[0]) {
      getAllVaultsForAdmin(user.vaultList[0].objectId, sessionToken).then(
        (result) => {
          setAllVaults(result.data.vaultList);
        }
      );
    }
  }, [user, sessionToken]);

  // Fetch Meta ad accounts on mount
  useEffect(() => {
    getMetaAdAccountsV23().then((res) => {
      setAdAccounts(res.data?.data ?? []);
    }).catch(() => {});
  }, []);

  // Fetch Facebook pages when ad account is selected
  useEffect(() => {
    if (!selectedAdAccount) {
      setFacebookPages([]);
      return;
    }
    getMetaFacebookPages(selectedAdAccount.id).then((res) => {
      console.log('Facebook pages response:', res.data);
      setFacebookPages(res.data?.data ?? []);
    }).catch((err) => {
      console.error('Failed to fetch Facebook pages:', err?.response?.data || err);
      setFacebookPages([]);
    });
  }, [selectedAdAccount]);

  // Fetch projects + logo when vault changes
  useEffect(() => {
    if (!selectedVault) {
      setProjects([]);
      setSelectedProject(null);
      setClientLogoDataUrl('');
      return;
    }

    getAllProjects(selectedVault.objectId, sessionToken).then((result) => {
      const sections = result.data?.projectSectionList ?? result.data ?? [];
      const flatProjects: any[] = [];
      if (Array.isArray(sections)) {
        sections.forEach((section: any) => {
          if (section.projectList) {
            flatProjects.push(...section.projectList);
          } else if (section.title || section.objectId) {
            flatProjects.push(section);
          }
        });
      }
      setProjects(flatProjects);
    });

    const logoUri = selectedVault.metadata?.logoUri;
    if (logoUri) {
      const baseImageUrl = getCurrentBackendEnvironment().baseImageUrl;
      fetchImageAsDataUrl(`${baseImageUrl}/${logoUri}`).then(
        setClientLogoDataUrl
      );
    } else {
      setClientLogoDataUrl('');
    }
  }, [selectedVault, sessionToken]);

  // Update location + fetch hero image when project changes
  useEffect(() => {
    if (selectedProject) {
      const addr = selectedProject.address;
      let loc = '';
      if (addr && typeof addr === 'object') {
        loc = [addr.city, addr.country].filter(Boolean).join(', ');
      } else if (typeof addr === 'string') {
        loc = addr;
      }
      setLocationText(loc);

      // Fetch hero images (up to 4 for variant thumbnails)
      if (selectedVault) {
        getProjectResources(selectedVault.objectId, selectedProject.objectId, 4)
          .then((result) => {
            const items = (result.data?.vmContentItemList ?? []).filter(
              (i: any) => i.contentItemType === 'image'
            );
            const baseImageUrl = getCurrentBackendEnvironment().baseImageUrl;
            return Promise.all(
              items.map((img: any) =>
                fetchImageAsDataUrl(`${baseImageUrl}/mr=1080,1920/${img.contentUri}`)
              )
            );
          })
          .then((urls) => setBackgroundDataUrls(urls.filter(Boolean)))
          .catch(() => setBackgroundDataUrls([]));
      }
    } else {
      setLocationText('');
      setBackgroundDataUrls([]);
    }
  }, [selectedProject, selectedVault]);

  const handleDownload = useCallback(async (variant: AdVariant) => {
    const el = variantRefs.current[variant];
    if (!el) return;
    setIsExporting(true);

    try {
      const dataUrl = await toPng(el, {
        width: 1080,
        height: 1920,
        pixelRatio: 1,
        cacheBust: true,
      });

      const link = document.createElement('a');
      const title = selectedProject?.title || selectedProject?.projectTitle || 'creative';
      const safeName = title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      link.download = `ad-${safeName}-${variant}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to export ad creative:', err);
    } finally {
      setIsExporting(false);
    }
  }, [selectedProject]);

  const handleDownloadAll = useCallback(async () => {
    for (const variant of ['classic', 'minimal', 'bold'] as AdVariant[]) {
      await handleDownload(variant);
    }
  }, [handleDownload]);

  // Auto-populate ad link URL when project/vault change
  useEffect(() => {
    if (selectedProject && selectedVault) {
      setAdLinkUrl(
        `https://my.prompto.com/projects/${selectedVault.objectId}/${selectedProject.objectId}`
      );
    } else {
      setAdLinkUrl('');
    }
  }, [selectedProject, selectedVault]);

  // Set default start date to today
  useEffect(() => {
    if (!startDate) {
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setEndDate(nextMonth.toISOString().split('T')[0]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCampaign = useCallback(async () => {
    if (!selectedAdAccount || !facebookPageId || !selectedProject) return;

    setIsCreating(true);
    setCreationError('');
    setCreationResult(null);

    const title = selectedProject.title || selectedProject.projectTitle || 'project';
    const safeName = title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const projectId = selectedProject.objectId;
    const adAccountId = selectedAdAccount.id;

    try {
      // Step 1: Generate PNGs from all 3 variants
      setCreationStep('Generating ad images...');
      const variants: AdVariant[] = ['classic', 'minimal', 'bold'];
      const pngDataUrls: string[] = [];
      for (const variant of variants) {
        const el = variantRefs.current[variant];
        if (!el) throw new Error(`Variant ref not found: ${variant}`);
        const dataUrl = await toPng(el, {
          width: 1080,
          height: 1920,
          pixelRatio: 1,
          cacheBust: true,
        });
        pngDataUrls.push(dataUrl);
      }

      // Step 2: Upload images to Meta
      const imageHashes: string[] = [];
      for (let i = 0; i < pngDataUrls.length; i++) {
        setCreationStep(`Uploading image ${i + 1}/3...`);
        const base64 = pngDataUrls[i].split(',')[1];
        const res = await uploadMetaAdImage(adAccountId, base64);
        const images = res.data?.images;
        const hash = images?.[Object.keys(images)[0]]?.hash;
        if (!hash) throw new Error(`Failed to get image hash for variant ${variants[i]}`);
        imageHashes.push(hash);
      }

      // Step 3: Create campaign
      setCreationStep('Creating campaign...');
      const campaignRes = await createMetaCampaign(adAccountId, {
        name: `${projectId}_${safeName}_campaign`,
        objective: 'OUTCOME_TRAFFIC',
        status: 'PAUSED',
      });
      const campaignId = campaignRes.data?.id;
      if (!campaignId) throw new Error('Failed to create campaign');

      // Step 4: Create ad set
      setCreationStep('Creating ad set...');
      const budgetCents = Math.round(parseFloat(dailyBudget) * 100);
      const adSetRes = await createMetaAdSet(adAccountId, {
        name: `${projectId}_${safeName}_adset`,
        campaign_id: campaignId,
        daily_budget: budgetCents,
        billing_event: 'IMPRESSIONS',
        optimization_goal: 'LINK_CLICKS',
        bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
        start_time: startDate,
        end_time: endDate,
        targeting: {
          geo_locations: {
            countries: targetCountries.map((c: any) => c.value),
          },
        },
        status: 'PAUSED',
      });
      const adSetId = adSetRes.data?.id;
      if (!adSetId) throw new Error('Failed to create ad set');

      // Step 5: Create 3 ad creatives
      const creativeIds: string[] = [];
      for (let i = 0; i < variants.length; i++) {
        setCreationStep(`Creating creative ${i + 1}/3...`);
        const creativeRes = await createMetaAdCreative(adAccountId, {
          name: `${projectId}_${safeName}_creative_${variants[i]}`,
          object_story_spec: {
            page_id: facebookPageId,
            link_data: {
              image_hash: imageHashes[i],
              link: adLinkUrl,
              message: adBodyText || description || title,
              call_to_action: {
                type: 'LEARN_MORE',
                value: { link: adLinkUrl },
              },
            },
          },
        });
        const creativeId = creativeRes.data?.id;
        if (!creativeId) throw new Error(`Failed to create creative for ${variants[i]}`);
        creativeIds.push(creativeId);
      }

      // Step 6: Create 3 ads
      const adIds: string[] = [];
      for (let i = 0; i < variants.length; i++) {
        setCreationStep(`Creating ad ${i + 1}/3...`);
        const adRes = await createMetaAd(adAccountId, {
          name: `${projectId}_${safeName}_ad_${variants[i]}`,
          adset_id: adSetId,
          creative: { creative_id: creativeIds[i] },
          status: 'PAUSED',
        });
        const adId = adRes.data?.id;
        if (!adId) throw new Error(`Failed to create ad for ${variants[i]}`);
        adIds.push(adId);
      }

      setCreationResult({ campaignId, adSetId, creativeIds, adIds });
      setCreationStep('');
    } catch (err: any) {
      const metaError = err?.response?.data?.error?.message;
      setCreationError(metaError || err.message || 'Campaign creation failed');
      setCreationStep('');
    } finally {
      setIsCreating(false);
    }
  }, [selectedAdAccount, facebookPageId, selectedProject, dailyBudget, startDate, endDate, targetCountries, adLinkUrl, adBodyText, description]);

  const projectTitle =
    selectedProject?.title || selectedProject?.projectTitle || 'Project Title';

  const vaultOptions = allVaults.map((v: any) => ({
    value: v.objectId,
    label: v.name || v.objectId,
    data: v,
  }));

  const projectOptions = projects.map((p: any) => ({
    value: p.objectId,
    label: p.title || p.projectTitle || p.objectId,
    data: p,
  }));

  return (
    <Container>
      <NavigationBar />
      <PageHeader>
        <PageTitle>Ad Creator</PageTitle>
      </PageHeader>

      <ContentLayout>
        <Sidebar>
          <SectionCard>
            <SectionTitle>Configuration</SectionTitle>

            <InputGroup>
              <InputLabel>Company</InputLabel>
              <Select
                options={vaultOptions}
                onChange={(option: any) => {
                  setSelectedVault(option?.data ?? null);
                  setSelectedProject(null);
                }}
                isClearable
                placeholder="Select a company..."
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Project</InputLabel>
              <Select
                options={projectOptions}
                onChange={(option: any) =>
                  setSelectedProject(option?.data ?? null)
                }
                isClearable
                isDisabled={!selectedVault}
                placeholder="Select a project..."
                value={
                  selectedProject
                    ? {
                        value: selectedProject.objectId,
                        label:
                          selectedProject.title ||
                          selectedProject.projectTitle ||
                          selectedProject.objectId,
                        data: selectedProject,
                      }
                    : null
                }
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Location</InputLabel>
              <Input
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder="e.g. Brussels, Belgium"
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Description</InputLabel>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Investeer in studentenkoten"
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Call to Action</InputLabel>
              <Input
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
              />
            </InputGroup>
          </SectionCard>

          <DownloadButton
            onClick={handleDownloadAll}
            disabled={!selectedProject || isExporting}
            loading={isExporting}
          >
            <FontAwesomeIcon
              icon={(isExporting ? faSpinner : faDownload) as any}
              spin={isExporting}
            />
            {isExporting ? 'Exporting...' : 'Download All (3 PNGs)'}
          </DownloadButton>

          <SectionCard style={{ marginTop: SPACING }}>
            <SectionTitle>Meta Campaign Settings</SectionTitle>

            <InputGroup>
              <InputLabel>Ad Account</InputLabel>
              <Select
                options={adAccounts.map((a: any) => ({
                  value: a.id,
                  label: a.name || a.id,
                  data: a,
                }))}
                onChange={(option: any) =>
                  setSelectedAdAccount(option?.data ?? null)
                }
                isClearable
                placeholder="Select ad account..."
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Facebook Page</InputLabel>
              {facebookPages.length > 0 ? (
                <Select
                  options={facebookPages.map((p: any) => ({
                    value: p.id,
                    label: p.name || p.id,
                  }))}
                  onChange={(option: any) =>
                    setFacebookPageId(option?.value ?? '')
                  }
                  isClearable
                  placeholder="Select Facebook page..."
                  value={
                    facebookPageId
                      ? {
                          value: facebookPageId,
                          label:
                            facebookPages.find((p: any) => p.id === facebookPageId)?.name ||
                            facebookPageId,
                        }
                      : null
                  }
                />
              ) : (
                <Input
                  value={facebookPageId}
                  onChange={(e) => setFacebookPageId(e.target.value)}
                  placeholder="e.g. 123456789012345"
                />
              )}
            </InputGroup>

            <InputGroup>
              <InputLabel>Daily Budget (EUR)</InputLabel>
              <Input
                type="number"
                min="1"
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Start Date</InputLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>End Date</InputLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </InputGroup>
          </SectionCard>

          <SectionCard>
            <SectionTitle>Targeting & Ad Content</SectionTitle>

            <InputGroup>
              <InputLabel>Target Countries</InputLabel>
              <Select
                isMulti
                options={COUNTRY_OPTIONS}
                value={targetCountries}
                onChange={(selected: any) =>
                  setTargetCountries(selected ?? [])
                }
                placeholder="Select countries..."
              />
              <HousingNote>
                Housing ads restrict targeting to country-level only (no age, gender, or zip code).
              </HousingNote>
            </InputGroup>

            <InputGroup>
              <InputLabel>Destination URL</InputLabel>
              <Input
                value={adLinkUrl}
                onChange={(e) => setAdLinkUrl(e.target.value)}
                placeholder="https://..."
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Ad Body Text</InputLabel>
              <Input
                value={adBodyText}
                onChange={(e) => setAdBodyText(e.target.value)}
                placeholder="Text shown above the ad image"
              />
            </InputGroup>
          </SectionCard>

          <CreateButton
            onClick={handleCreateCampaign}
            disabled={
              !selectedProject ||
              !selectedAdAccount ||
              !facebookPageId ||
              !dailyBudget ||
              !adLinkUrl ||
              targetCountries.length === 0 ||
              isCreating
            }
            loading={isCreating}
          >
            <FontAwesomeIcon
              icon={(isCreating ? faSpinner : faRocket) as any}
              spin={isCreating}
            />
            {isCreating ? creationStep || 'Creating...' : 'Create Meta Campaign'}
          </CreateButton>

          {creationResult && (
            <StatusBox variant="success">
              <FontAwesomeIcon icon={faCheck as any} /> Campaign created successfully!
              <br />
              Campaign ID: {creationResult.campaignId}
              <br />
              Ad Set ID: {creationResult.adSetId}
              <br />
              Ads: {creationResult.adIds.join(', ')}
            </StatusBox>
          )}

          {creationError && (
            <StatusBox variant="error">
              <FontAwesomeIcon icon={faExclamationTriangle as any} /> {creationError}
            </StatusBox>
          )}
        </Sidebar>

        <PreviewArea>
          {(['classic', 'minimal', 'bold'] as AdVariant[]).map((v, i) => (
            <PreviewCard key={v}>
              <VariantLabel>{v}</VariantLabel>
              <PreviewContainer>
                <ScaleWrapper>
                  <div ref={(el) => { variantRefs.current[v] = el; }}>
                    <AdTemplate
                      variant={v}
                      projectTitle={projectTitle}
                      projectLocation={locationText}
                      description={description}
                      logoUrl={PromptoLogo}
                      clientLogoUrl={clientLogoDataUrl}
                      backgroundUrl={backgroundDataUrls[i] || backgroundDataUrls[0] || ''}
                      ctaText={ctaText}
                    />
                  </div>
                </ScaleWrapper>
              </PreviewContainer>
              <DownloadButton
                onClick={() => handleDownload(v)}
                disabled={!selectedProject || isExporting}
                loading={isExporting}
                style={{ width: 'auto', padding: '8px 16px', fontSize: '0.75rem' }}
              >
                <FontAwesomeIcon icon={faDownload as any} />
                Download
              </DownloadButton>
            </PreviewCard>
          ))}
        </PreviewArea>
      </ContentLayout>
    </Container>
  );
};

export default AdsPage;
